<template>
    <div class="modal-overlay" v-if="show" @click.self="close">
        <div class="modal-content">
            <div class="modal-header">
                <h3>{{ $t("admin.moderationReview.title") }}</h3>
                <button class="modal-close" @click="close">
                    <i class="icon-close" />
                </button>
            </div>

            <div class="modal-body">
                <div class="detail-row">
                    <span class="detail-label">{{ $t("admin.moderationReview.postType") }}</span>
                    <span class="detail-value">{{ getPostTypeLabel(request.postType) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">{{ $t("admin.moderationReview.reportedBy") }}</span>
                    <span class="detail-value">{{ request.reportedByName || request.reportedBy }}</span>
                </div>

                <div class="content-section">
                    <span class="detail-label">{{ $t("admin.moderationReview.content") }}</span>
                    <div class="content-box">
                        <TipTap :value="processedContent" :readonly="true" :showSaveButton="false" />
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <button class="btn btn-delete" @click="handleAction('delete')">
                    {{ $t("admin.moderationReview.actions.delete") }}
                </button>
                <button class="btn btn-keep" @click="handleAction('keep')">
                    {{ $t("admin.moderationReview.actions.keep") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from "vue";
import TipTap from "@/views/shared/Tiptap.vue";
import { PostType } from "@/api/api";

interface ModerationRequest {
    id: number;
    postType: PostType;
    reportedBy: string;
    reportedByName?: string;
}

export default Vue.extend({
    name: "ModerationReviewModal",
    components: {
        TipTap,
    },
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        request: {
            type: Object as () => ModerationRequest,
            required: true,
        },
        content: {
            type: String,
            required: true,
        },
    },
    computed: {
        processedContent(): any {
            if (!this.content) {
                return null;
            }
            try {
                return JSON.parse(this.content);
            } catch (error) {
                return {
                    type: "doc",
                    content: [
                        {
                            type: "paragraph",
                            content: [
                                {
                                    type: "text",
                                    text: this.content,
                                },
                            ],
                        },
                    ],
                };
            }
        },
    },
    methods: {
        close(): void {
            this.$emit("close");
        },
        async handleAction(action: "delete" | "keep"): Promise<void> {
            try {
                await this.$services.api.acknowledgeModerationRequest(this.request.id, action);
                this.$emit("action-complete");
                this.close();
            } catch (error) {
                console.error("Error handling moderation action:", error);
            }
        },
        getPostTypeLabel(postType: PostType): string {
            switch (postType) {
                case PostType.DISCUSSION_POST:
                    return this.$tc("admin.moderationReview.postTypes.discussionPost");
                case PostType.DATA_EVENT:
                    return this.$tc("admin.moderationReview.postTypes.dataEvent");
                default:
                    return postType;
            }
        },
    },
});
</script>

<style scoped lang="scss">
@use "src/scss/variables";

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: variables.$z-index-menu;
}

.modal-content {
    background: #fff;
    border-radius: 3px;
    max-width: 600px;
    width: 90%;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-height: 80vh;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--color-border);

    h3 {
        font-family: var(--font-family-bold);
        font-size: 18px;
        margin: 0;
    }
}

.modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: #6a6d71;
    font-size: 14px;
    transition: color 0.15s ease;

    &:hover {
        color: var(--color-dark);
    }
}

.modal-body {
    padding: 24px;
    overflow-y: auto;
}

.detail-row {
    display: flex;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 14px;
}

.detail-label {
    font-size: 13px;
    color: #6a6d71;
    white-space: nowrap;
}

.detail-value {
    font-size: 14px;
    font-family: var(--font-family-medium);
}

.content-section {
    margin-top: 20px;

    .detail-label {
        display: block;
        margin-bottom: 8px;
    }
}

.content-box {
    background: #f4f5f7;
    padding: 16px;
    border-radius: 3px;
    border: 1px solid var(--color-border);
    max-height: 250px;
    overflow-y: auto;
}

.modal-footer {
    display: flex;
    gap: 10px;
    justify-content: space-between;
    padding: 16px 24px;
    border-top: 1px solid var(--color-border);
}

.btn {
    padding: 8px 20px;
    font-size: 13px;
    font-family: var(--font-family-bold);
    border: none;
    border-radius: 3px;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.9;
    }
}

.btn-keep {
    background: #3f8530;
    color: #fff;
}

.btn-delete {
    background: var(--color-danger);
    color: #fff;
}
</style>
